import { useNavigate } from "react-router";
import { IListItemProps } from "../interfaces";
import { useEffect, useState } from "react";
import apiAgent from "../../api/apiAgent";
import { tMsg } from "../ToastNotifications";
import { mergeClass, parseDate, pause, removeTags } from "../../utils";
import _ from "lodash";
import { Spinner } from "../Loader";
import { DeletePopup } from "../ConfirmationPopup";
import { DefaultListItemDisplay } from "./DefaultDisplay";
import { SlidePreviewDisplay } from "./PreviewDisplay";
import { useSetChangesMade } from "../Publishing/utils";
import { Tooltip } from "../Tooltip";

// const fileNameFromUrl = (url: string) =>
//   url.substring(url.lastIndexOf("/" + 1));

export const ItemWithPublish = ({
  item,
  modelName,
  returnModel,
  returnId,
  refreshList,
  linkParams,
}: IListItemProps): JSX.Element => {
  const navigate = useNavigate();
  const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
  const [publishing, setPublishing] = useState<boolean>(false);
  const setChangesMade = useSetChangesMade(modelName);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);

  useEffect(() => {
    setImageSrc(
      item.thumbUrl ||
        item.thumbnail ||
        item.thumbnailUrl ||
        item.imageUrl ||
        item.fullImageUrl ||
        undefined
    );
  }, [item]);

  const itemName = item.cardTitle || removeTags(item.caption || item.type);

  const userRole = modelName === "users" ? item.user_role : undefined;

  const handleDeletePressed = async () => {
    try {
      if (!item.id) throw "No ID specified.";
      const request =
        modelName === "users"
          ? apiAgent.Users.delete(modelName, { username: item.name })
          : apiAgent.DataType.delete(modelName, {
              targetIdx: _.toNumber(item.id),
            });
      const response = await request;
      if (response.status === 200) {
        tMsg.success(`Slide deleted.`);
        if (refreshList) refreshList();
        setChangesMade();
      }
    } catch (err) {
      // TODO: add any error in response to notifications here
      tMsg.error(`Error deleting slide`);
      console.log(err);
    }
  };

  const handlePublishPressed = async () => {
    try {
      if (!item.id) throw "No ID specified";
      setPublishing(true);
      await pause(300);
      const time = new Date().toISOString();
      const published = !item.published;
      const response = await apiAgent.DataType.update(modelName, {
        ...item,
        published,
        publishedAt: time,
        updatedAt: time,
        targetIdx: item.id,
        donorList: item.donorList?.map((d: string) => d.replaceAll('"', "")),
      });
      if (response.status === 200) {
        tMsg.success(
          published ? `Slide set to visible` : `Slide set to hidden`
        );
        if (refreshList) refreshList();
        setChangesMade();
      }
    } catch (err) {
      tMsg.error(`Error toggling visibility`);
      console.log(err);
    } finally {
      setPublishing(false);
    }
  };

  const addParams =
    returnModel && returnId
      ? `?from=${returnModel}&id=${returnId}&${linkParams}`
      : `?${linkParams}`;

  const handleEditPressed = () =>
    navigate(`/m/${modelName}/edit/${item.id}${addParams}`);
  const handleMainNavigate = () =>
    navigate(`/m/${modelName}/show/${item.id}${addParams}`);

  const hasDates = item.displayDates && item.displayDates.length == 2;
  const visibility = () =>
    !item.published ? (
      <div className="font-medium text-sm">Hidden</div>
    ) : !hasDates ? (
      <div className="font-medium text-sm">Visible</div>
    ) : (
      <div className="font-medium h-full text-sm flex gap-2 justify-center items-center">
        <span>Visible</span>
        <div className="w-2 h-2 bg-white rounded-full">
          <Tooltip>
            {`${new Date(Date.parse(item.displayDates[0])).toDateString()} -
            ${new Date(Date.parse(item.displayDates[1])).toDateString()}`}
          </Tooltip>
        </div>
      </div>
    );

  const Interface = () => (
    <>
      <div
        className={mergeClass(
          "absolute top-0 left-0 text-white py-0.5 px-2 cursor-pointer w-20",
          "hover:outline outline-1 -outline-offset-2 drop-shadow group/tooltip",
          // "group-hover:h-12",
          {
            "bg-successGreen/90": item.published,
            "bg-darkGrayBg/90": !item.published,
          }
        )}
        onClick={() => handlePublishPressed()}
      >
        {publishing ? <Spinner className="h-4 w-4" /> : visibility()}
      </div>
      <div
        className={mergeClass(
          "font-bold transition absolute top-0 right-0 flex opacity-0",
          "group-hover:opacity-100 group-hover:pointer-events-auto drop-shadow"
        )}
      >
        <div
          className="bg-white/90 text-infoBlue outline-1 -outline-offset-2 hover:outline p-3 cursor-pointer"
          onClick={() => handleEditPressed()}
        >
          <i className="icon-edit" />
        </div>
        <div
          className="bg-white/90 text-warningRed outline-1 -outline-offset-2 hover:outline p-3 cursor-pointer"
          onClick={() => setShowDeletePopup(true)}
        >
          <i className="icon-delete" />
        </div>
      </div>
      <DeletePopup
        confirmAction={() => handleDeletePressed()}
        setShowModal={setShowDeletePopup}
        showModal={showDeletePopup}
      />
    </>
  );

  const isCustomPreview = modelName !== "users";

  return (
    <div
      className={mergeClass("transition group relative border", {
        "w-48 h-64": !isCustomPreview,
        "w-fit h-fit": isCustomPreview,
      })}
    >
      <div
        onClick={() => handleMainNavigate()}
        className={mergeClass("flex flex-col cursor-pointer", {
          "w-full h-full": !isCustomPreview,
          "w-fit h-fit": isCustomPreview,
        })}
      >
        {isCustomPreview ? (
          <SlidePreviewDisplay
            itemName={itemName}
            item={item}
            modelName={modelName}
          />
        ) : (
          <DefaultListItemDisplay
            itemName={itemName}
            userRole={userRole}
            imageSrc={imageSrc}
          />
        )}
      </div>
      <Interface />
    </div>
  );
};
